import { useEffect, useMemo } from 'react';
import http from 'hub-http/clients/apiClient';
import { registerQuery, useQuery } from 'data-fetching-client';

import type { User } from 'ui-product-approvals-common';
import { captureException } from '../utils/sentryUtils';

const USERS_BY_SCOPE_FIELD = 'usersByScope';

type FetchUsersByScopeVariables = {
  scope: string;
};

const FETCH_USERS_BY_SCOPE = registerQuery<
  typeof USERS_BY_SCOPE_FIELD,
  FetchUsersByScopeVariables,
  User[]
>({
  fieldName: USERS_BY_SCOPE_FIELD,
  args: ['scope'],
  fetcher: ({ scope }) =>
    http.get(`permissions-skynet/v1/scopes/app/${scope}/hub-users`),
});

const sortUsers = (unsortedUsers: User[]) =>
  [...unsortedUsers].sort((a, b) =>
    a.firstName.toLocaleLowerCase() < b.firstName.toLocaleLowerCase() ? -1 : 1
  );

export const useFetchUsersByScope = (
  scope: string,
  options: { skip?: boolean } = {}
) => {
  const { data, loading, error } = useQuery(FETCH_USERS_BY_SCOPE, {
    variables: { scope },
    skip: options.skip,
  });

  useEffect(() => {
    if (error) {
      captureException(error, {
        extra: { fingerprint: 'useFetchUsersByScope' },
      });
    }
  }, [error]);

  const users = useMemo(() => {
    const rawUsers = data?.[USERS_BY_SCOPE_FIELD] ?? [];
    return sortUsers(rawUsers);
  }, [data]);

  return { isLoading: loading, users };
};
